// src/components/fnct/GesteActionEnPlace.tsx — SCE Issues
// Clore, reporter ou confier une action SANS ouvrir sa FNC-T (24/10, Guillaume :
// « au lieu d'ouvrir la FNC-T et d'arriver à cette zone » ; Confier depuis le
// 30/10) : le formulaire du plan d'actions de la fiche (FormCloture, FormReport,
// FormConfier), ouvert sous la rangée, dans « Mes FNC-T », la carte « Vos actions
// FNC-T » de l'accueil et le volet de la liste. Mêmes écritures que la fiche
// (relues, tracées, notifiées), même confirmation en bascule de rôle.

import { useState, type JSX } from 'react'
import { useSnackbar } from '@/components/ui/Snackbar'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import { cloreAction, confierAction, reporterAction } from '@/api/fnct.api'
import { ajouterJours } from '@/utils/escaladeFnct'
import { aujourdhuiParis } from '@/utils/fnct'
import { FormCloture, FormConfier, FormReport } from './PlanActions'
import type { ActionFnct } from '@/types/fnct.types'

export type GesteAction = 'clore' | 'reporter' | 'confier'

export function GesteActionEnPlace({
  action,
  geste,
  habitues,
  onFermer,
  onFait,
}: {
  action: ActionFnct
  geste: GesteAction
  /** Confier : les responsables d'actions les plus fréquents, proposés champ vide. */
  habitues?: readonly string[]
  onFermer: () => void
  /** Après l'écriture : relire les listes. */
  onFait: () => void
}): JSX.Element {
  const { user, nomReel } = useCurrentUser()
  const confirmerEcriture = useConfirmerEcriture()
  const snackbar = useSnackbar()
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const aujourdhui = aujourdhuiParis()
  const auteur = nomReel || user?.nom || ''

  async function ecrire(libelle: string, faire: () => Promise<void>, fait: string): Promise<void> {
    if (!action.itemId || !confirmerEcriture(libelle)) return
    setEnvoi(true)
    setErreur(null)
    try {
      await faire()
      snackbar.show(fait, 'success')
      onFermer()
      onFait()
    } catch (err) {
      setErreur(err instanceof Error ? err.message : String(err))
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div style={{ paddingBottom: 2 }}>
      {geste === 'clore' ? (
        <FormCloture
          aujourdhui={aujourdhui}
          min={action.ouverture}
          envoi={envoi}
          onAnnuler={onFermer}
          onValider={(date, commentaire) =>
            void ecrire('Clore une action', () => cloreAction(action.itemId ?? '', date, commentaire, aujourdhui, auteur), `${action.id} close.`)
          }
        />
      ) : geste === 'reporter' ? (
        <FormReport
          depart={ajouterJours(action.echeanceReportee ?? action.echeance ?? aujourdhui, 14)}
          min={(action.echeanceReportee ?? action.echeance) ? ajouterJours(action.echeanceReportee ?? action.echeance ?? aujourdhui, 1) : null}
          envoi={envoi}
          onAnnuler={onFermer}
          onValider={(date, motif) =>
            void ecrire('Reporter une action', () => reporterAction(action.itemId ?? '', date, motif, aujourdhui, auteur), `${action.id} reportée.`)
          }
        />
      ) : (
        <FormConfier
          action={action}
          habitues={habitues}
          envoi={envoi}
          onAnnuler={onFermer}
          onValider={(vers, motif) =>
            void ecrire('Confier une action', () => confierAction(action.itemId ?? '', action.responsable, vers, motif, aujourdhui, auteur), `${action.id} confiée à ${vers.nom}.`)
          }
        />
      )}
      {erreur && <p style={{ margin: '0 16px 12px', fontSize: 14, color: 'var(--color-error)' }}>{erreur}</p>}
    </div>
  )
}
